import React, { useCallback, useEffect, useState } from 'react';

/*
 * A fiók-oldal és az Admin › Bot közös darabjai (a sablon account.html-je szerint).
 * 2026-10-06-ig a UserPanel.jsx-ben éltek; azóta a bot részei (licenc, loader, eszköz,
 * kulcs) az adminpanelben vannak, és ott is ezekből épülnek.
 */

export const pad = (n) => String(n).padStart(2, '0');
export const utcStamp = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

export function Kicker({ id, children, aside, asideClass = 'text-gray-500' }) {
  return (
    <h2 id={id} className="kicker">
      {children}
      {aside && <span className={`font-mono text-xs font-normal normal-case tracking-normal ${asideClass}`}>{aside}</span>}
    </h2>
  );
}

/** Kártya sorokkal (a sablon `.rows` / `.row`-ja): címke · érték · gombok. */
export function Rows({ children, className = '' }) {
  return <div className={`card overflow-hidden ${className}`}>{children}</div>;
}

export function Row({ k, children, acts, mono = false }) {
  return (
    <div className="min-h-[56px] px-[18px] py-2.5 grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[130px_minmax(0,1fr)_auto]
                    items-center gap-x-5 gap-y-1 border-t border-line-faint first:border-t-0">
      <span className="col-span-2 sm:col-span-1 text-[13px] text-gray-500">{k}</span>
      <span className={`min-w-0 text-gray-50 ${mono ? 'font-mono text-[13px]' : 'text-sm'}`}>{children}</span>
      <span className="flex justify-end gap-2">{acts}</span>
    </div>
  );
}

export function RowNote({ children }) {
  return <p className="px-[18px] py-3 border-t border-line-faint text-[12.5px] text-gray-500">{children}</p>;
}

export function Chip({ tone = 'neutral', children }) {
  const cls = {
    neutral: 'text-gray-400 border-line-strong',
    acc: 'bg-brand-500/[0.12] text-brand-400 border-brand-500/30',
    ok: 'bg-ok/[0.08] text-ok border-ok/35',
    warn: 'bg-warn/[0.08] text-warn border-warn/35',
    bad: 'bg-danger/[0.08] text-[#ff8a80] border-danger/35',
    info: 'bg-info/[0.08] text-info border-info/35',
  }[tone];
  return <span className={`badge ${cls}`}>{children}</span>;
}

/** Hátralévő idő négy dobozban (nap / óra / perc / mp), másodpercenként frissül. */
export function Countdown({ expiresAt, frozen }) {
  const calc = useCallback(() => Math.max(0, Date.parse(expiresAt) - Date.now()), [expiresAt]);
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    if (frozen) return undefined;
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [calc, frozen]);

  const s = Math.floor(left / 1000);
  const parts = [
    [Math.floor(s / 86400), 'days'],
    [pad(Math.floor((s % 86400) / 3600)), 'hours'],
    [pad(Math.floor((s % 3600) / 60)), 'min'],
    [pad(s % 60), 'sec'],
  ];
  const tone = s < 86400 ? 'text-[#ff8a80]' : s < 3 * 86400 ? 'text-warn' : 'text-gray-50';
  return (
    <div className="flex gap-2" aria-label="Time left" role="timer">
      {parts.map(([v, l]) => (
        <div key={l} className="flex-1 sm:flex-none sm:min-w-[64px] px-2 pt-2.5 pb-2 rounded-panel bg-bar border border-line text-center">
          <b className={`block font-mono text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${tone}`}>{v}</b>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-gray-500">{l}</span>
        </div>
      ))}
    </div>
  );
}

export function KV({ k, children, mono = false }) {
  return (
    <div className="py-2.5 grid grid-cols-[110px_minmax(0,1fr)] gap-3 text-sm border-t border-line-faint first:border-t-0">
      <dt className="text-[13px] text-gray-500">{k}</dt>
      <dd className={`m-0 min-w-0 text-gray-50 ${mono ? 'font-mono text-[13px]' : ''}`}>{children}</dd>
    </div>
  );
}

/**
 * Az előfizetés állapota egy cédulán. `updating`: a bot épp frissül (csak az Admin ›
 * Bot licenc-kártyáján számít); `activeLabel`: a futó előfizetés felirata.
 */
export function LicenceTag({ sub, updating = false, activeLabel = 'Running' }) {
  if (!sub) return null;
  if (sub.lifetime) return <span className="tag tag-ok">Lifetime</span>;
  if (Date.parse(sub.expires_at) < Date.now()) return <span className="tag">Expired</span>;
  if (sub.is_frozen) return <span className="tag tag-info">Frozen</span>;
  if (updating) return <span className="tag tag-warn">Bot updating</span>;
  return <span className="tag tag-ok">{activeLabel}</span>;
}
